import { useCallback, useEffect, useRef, useState } from 'react';

import type { IGoogleAdsKeywordIdea } from '@feathr/blackbox';
import { useStore } from '@feathr/extender/state';

import { getSeedSignature } from './KeywordFields.utils';

export interface IUseKeywordIdeas {
  ideas: IGoogleAdsKeywordIdea[];
  isLoading: boolean;
  hasError: boolean;
  /** Whether suggestions have been asked for at least once. */
  hasFetched: boolean;
  /** Whether the keywords have changed since the last fetch, so asking again is worthwhile. */
  canRefresh: boolean;
  fetchIdeas: () => void;
}

/**
 * Google's suggested keywords for one ad group, fetched only when asked for.
 *
 * Nothing is fetched on mount, deliberately: every call spends from the Google quota this
 * work exists to protect, and most editor visits are not about keywords at all. The user
 * asks, and can ask again once they have added keywords worth re-seeding with.
 *
 * Failure is reported rather than thrown: suggestions are an aid, and the caller keeps
 * manual keyword entry working in every state.
 *
 * @param adGroupId - The saved ad group's id, or undefined before its first save.
 * @param seedKeywords - The keywords to seed with, taken from what the editor shows now.
 * @returns The suggestions, the request flags, and the trigger.
 */
export function useKeywordIdeas(adGroupId?: string, seedKeywords: string[] = []): IUseKeywordIdeas {
  const seedSignature = getSeedSignature(seedKeywords);
  const { GoogleAdsAdGroups } = useStore();
  const [ideas, setIdeas] = useState<IGoogleAdsKeywordIdea[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [fetchedSignature, setFetchedSignature] = useState<string | null>(null);
  // Ignores repeat presses while a request is already running, rather than stacking calls.
  const isFetching = useRef(false);
  /*
   * Google takes around five seconds, which is ample time to close the editor. Without
   * this the response lands on an unmounted component and React warns.
   */
  const isMounted = useRef(true);
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const fetchIdeas = useCallback((): void => {
    if (!adGroupId || isFetching.current) {
      return;
    }

    isFetching.current = true;
    setIsLoading(true);
    setHasError(false);

    GoogleAdsAdGroups.getKeywordIdeas(adGroupId, seedKeywords)
      .then((fetched) => {
        if (isMounted.current) {
          setIdeas(fetched);
        }
      })
      .catch(() => {
        if (isMounted.current) {
          setIdeas([]);
          setHasError(true);
        }
      })
      .finally(() => {
        isFetching.current = false;
        if (isMounted.current) {
          setIsLoading(false);
          // Recorded even on failure, so the button reads "Refresh" and stays actionable.
          setFetchedSignature(seedSignature);
        }
      });
    // seedKeywords is a new array every render; seedSignature already captures its content, so
    // including seedKeywords here would recreate this callback every render for no reason.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adGroupId, seedSignature, GoogleAdsAdGroups]);

  return {
    ideas,
    isLoading,
    hasError,
    hasFetched: fetchedSignature !== null,
    canRefresh: fetchedSignature !== null && fetchedSignature !== seedSignature,
    fetchIdeas,
  };
}
